import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from '@digital-twins/design-system/icons'
import { SocialUniverseItem } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CollectionTopicCard } from '@/components/library/CollectionTopicCard'
import { collectionSectionId } from '@/components/library/CollectionKpiStrip'
import { SocialBarList, SocialBarListRow } from '@/pages/KnowledgeHubPage/social/charts/SocialBarList'
import { sharesLargestRemainder } from '@/pages/KnowledgeHubPage/social/charts/socialTopicMetrics'
import { SocialTopicPanelHeader } from '@/pages/KnowledgeHubPage/social/SocialTopicPanelHeader'
import { formatSocialCount } from '@/pages/KnowledgeHubPage/social/socialFormat'
import { useSocialUniverses } from '@/pages/KnowledgeHubPage/social/useSocialUniverses'
import { ROUTES } from '@/routes/routes'
import { CollectionSectionTitle } from './CollectionSectionTitle'
import { collectionCardLayout, collectionSectionGridClass } from './collectionSectionLayout'
import { useCollectionTopics } from './useCollectionTopics'

export interface CollectionTopicsAreaProps {
  collectionToken: string
  /** Handed to each Topic link, so the Topic page's back link names the collection. */
  collectionName?: string
  /** The viewer can edit the collection: only then is Add shown (the picker's writes need it). */
  canEdit: boolean
  /** Opens the add picker on its Topics tab. */
  onAdd: () => void
  /** Phase B's scoped theme row; renders nothing in A. */
  themeRow?: ReactNode
}

/**
 * The collection's Topics, in the page's card language: a
 * `CollectionTopicCard` per Topic in the resources' grid, drawing post
 * images, market and channel health from the org's Topic list where it
 * holds the Topic (a generated cover where it does not, or the list
 * failed). Each card is one link (no nested actions: Topics are not managed
 * from a collection). With two or more
 * Topics, "Posts by Topic" as a bar list in a hairline panel. A skeleton
 * while loading, a muted error with Try again on a failed read, and hidden
 * when the org has social listening off.
 */
export function CollectionTopicsArea(props: CollectionTopicsAreaProps) {
  const { collectionToken, collectionName, canEdit, onAdd, themeRow } = props

  const { t, i18n } = useTranslation('app')
  const { topics, socialListeningEnabled, isLoading, hasError, refresh } = useCollectionTopics(collectionToken)
  const socialUniverses = useSocialUniverses(socialListeningEnabled && topics.length > 0)
  const universesByToken = useMemo(() => {
    const map = new Map<string, SocialUniverseItem>()
    for (const universe of socialUniverses.universes) {
      map.set(universe.token, universe)
    }
    return map
  }, [socialUniverses.universes])

  if (isLoading || socialUniverses.isLoading) {
    return (
      <section aria-label={t('library.collections.topics.title')} data-testid="collection-topics-loading">
        <Skeleton className="h-40 rounded-2xl border border-card-border bg-background" />
      </section>
    )
  }

  if (hasError && !socialListeningEnabled) {
    return (
      <section
        aria-label={t('library.collections.topics.title')}
        className="flex flex-col gap-4"
        data-testid="collection-topics-area"
      >
        <CollectionSectionTitle title={t('library.collections.topics.title')} testIdPrefix="collection-topics" />
        <div className="flex items-center gap-3" data-testid="collection-topics-error">
          <p className="text-sm text-muted-foreground">{t('library.collections.topics.loadFailed')}</p>
          <Button
            variant="outline"
            size="sm"
            shape="pill"
            onClick={() => void refresh()}
            data-testid="collection-topics-error-retry"
          >
            {t('library.collections.tryAgain')}
          </Button>
        </div>
      </section>
    )
  }

  if (!socialListeningEnabled) {
    return null
  }

  const sortedTopics = [...topics].sort((a, b) => b.posts - a.posts || a.name.localeCompare(b.name))
  const shares = sharesLargestRemainder(sortedTopics.map((topic) => topic.posts))
  const maxPosts = Math.max(1, ...sortedTopics.map((topic) => topic.posts))
  const barRows: Array<SocialBarListRow> = sortedTopics.map((topic, index) => ({
    key: topic.token,
    label: topic.name,
    count: topic.posts,
    share: (topic.posts * 100) / maxPosts,
    valueLabel: formatSocialCount(topic.posts, i18n.language),
    detailLabel: t('socialListening.topicKpis.percent', { value: shares?.[index] ?? 0 }),
  }))

  const barList =
    topics.length >= 2 ? (
      <section
        aria-label={t('library.collections.topics.postsByTopic')}
        className="rounded-[calc(var(--radius)+8px)] border border-card-border bg-background pb-4"
        data-testid="collection-topics-bars"
      >
        <SocialTopicPanelHeader
          title={t('library.collections.topics.postsByTopic')}
          trailing={t('library.collections.topics.postsByTopicNote')}
        />
        <div className="px-3 pt-2">
          <SocialBarList
            rows={barRows}
            testIdPrefix="collection-topics-bar"
            ariaLabel={t('library.collections.topics.postsByTopic')}
          />
        </div>
      </section>
    ) : null

  return (
    <section
      id={collectionSectionId('topics')}
      aria-label={t('library.collections.topics.title')}
      className="flex scroll-mt-20 flex-col gap-6"
      data-testid="collection-topics-area"
    >
      <CollectionSectionTitle
        title={t('library.collections.topics.title')}
        count={topics.length > 0 ? formatSocialCount(topics.length, i18n.language) : null}
        trailing={
          canEdit ? (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={onAdd} data-testid="collection-topics-add">
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('library.collections.topics.addTopics')}
            </Button>
          ) : null
        }
        testIdPrefix="collection-topics"
      />
      {topics.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="collection-topics-empty">
          {t('library.collections.topics.empty')}
        </p>
      ) : (
        <div className={collectionSectionGridClass(topics.length)} data-testid="collection-topics-grid">
          {topics.map((topic, index) => (
            <CollectionTopicCard
              key={topic.token}
              topic={topic}
              index={index}
              universe={universesByToken.get(topic.token)}
              layout={collectionCardLayout(index, topics.length)}
              backLabel={collectionName}
              backHref={ROUTES.knowledgeCollection(collectionToken)}
            />
          ))}
        </div>
      )}
      {barList}
      {themeRow ? <div data-testid="collection-topics-theme-row">{themeRow}</div> : null}
    </section>
  )
}
